import { browser } from '$app/environment';
import { toastDisplay, decodeJwt, removeToast } from '$lib/utils.svelte.js';
import {
	userSession,
	userProStatus,
	userFreeUsage,
	apiRootUrl,
	userDocuments,
	userTags,
	currentConversationData,
	currentDocument
} from './stores.svelte.js';
import { userOrg } from '$lib/organisation.svelte.js';
import { get } from 'svelte/store';
import posthog from 'posthog-js';
import { t } from '$lib/i18n.svelte.js';
import { promoConfigs } from './data/promoConfig.js';

function getBTOBAPIURL() {
	return browser ? (get(apiRootUrl) || 'api.ordalie.tech')
		.replace('5001', '5002')
		.replace('api.ordalie.tech', 'b2b.ordalie.tech') : "api.ordalie.tech";
}

/*------------------------------------*\
		HELPER FUNCTIONS
/*------------------------------------*/

/**
 * Generic API request wrapper with loading toast and error handling
 * @param {string} url - The API endpoint URL
 * @param {RequestInit} options - Fetch options
 * @param {string} loadingMessage - Message to show in loading toast
 * @param {string} [errorMessage] - Custom error message
 * @returns {Promise<any>}
 */
async function apiRequest(url, options = {}, loadingMessage = '', errorMessage = '') {
	const loadingToastId = loadingMessage ? toastDisplay(loadingMessage, 'loading', 0, undefined, 'bottom-center', true) : null;
	
	try {
		const response = await fetch(url, options);
		if (loadingToastId) removeToast(loadingToastId);
		
		if (!response.ok) {
			const errorText = await response.text();
			const message = errorMessage || t('error.generalErrorMsg');
			toastDisplay(message, 'error');
			throw new Error(`Request failed: ${response.status} ${errorText}`);
		}
		
		return await response.json();
	} catch (error) {
		if (loadingToastId) removeToast(loadingToastId);
		if (!errorMessage) {
			toastDisplay(t('error.generalErrorMsg'), 'error');
		}
		throw error;
	}
}

/**
 * Create authenticated headers
 * @param {string | null} token - Access token
 * @returns {Record<string, string>}
 */
function createAuthHeaders(token) {
	/** @type {Record<string, string>} */
	const headers = {
		'Accept': 'application/json',
		'Content-Type': 'application/json'
	};
	
	if (token) {
		headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
	}
	
	return headers;
}

/**
 * Store authentication data
 * @param {any} data - Auth response data
 */
function storeAuth(data) {
	if (data.access_token) localStorage.setItem('accessToken', data.access_token);
	if (data.refresh_token) localStorage.setItem('refreshToken', data.refresh_token);
	if (data.user) userSession.set(data.user);
	if ('org' in data) userOrg.set(data.org);
}

/**
 * Clear all authentication state
 */
function clearAuthState() {
	// @ts-ignore
	userSession.set(null);
	// @ts-ignore
	userOrg.set(null);
	// @ts-ignore
	currentConversationData.set(null);
	// @ts-ignore
	currentDocument.set(null);
	// @ts-ignore
	userDocuments.set(null);
	userTags.set([]);
	// @ts-ignore
	userFreeUsage.set(null);
	// @ts-ignore
	userProStatus.set('none');
	
	const keysToRemove = [
		'accessToken', 'refreshToken', 'currentConv', 'userOrg',
		'mfaToken', 'tempMfaToken', 'mfa_temp_token'
	];
	
	keysToRemove.forEach(key => localStorage.removeItem(key));
}

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @param {number} bufferSeconds - Buffer time in seconds before considering token expired
 * @returns {boolean}
 */
function isTokenExpired(token, bufferSeconds = 30) {
	try {
		const decoded = decodeJwt(token);
		// @ts-ignore
		const { exp } = decoded;
		const now = Date.now().valueOf() / 1000;
		return exp < now + bufferSeconds;
	} catch {
		return true;
	}
}

/**
 * Enhanced fetch wrapper that automatically handles token refresh and retries
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options
 * @param {boolean} autoRetry - Whether to automatically retry on 401 errors
 * @returns {Promise<Response>}
 */
export async function authenticatedFetch(url, options = {}, autoRetry = true) {
	const accessToken = await getAccessToken();
	
	if (!accessToken) {
		throw new Error('No access token available');
	}

	const headers = {
		...options.headers,
		'Authorization': `Bearer ${accessToken}`
	};

	const response = await fetch(url, {
		...options,
		headers
	});

	// If we get a 401 and auto-retry is enabled, try to refresh the token and retry once
	if (response.status === 401 && autoRetry) {
		console.log('Received 401, attempting token refresh and retry');
		
		// Force a token refresh
		const newToken = await refreshAccessToken();
		
		if (newToken) {
			// Retry the request with the new token
			const retryHeaders = {
				...options.headers,
				'Authorization': `Bearer ${newToken}`
			};
			
			return fetch(url, {
				...options,
				headers: retryHeaders
			});
		}
	}

	return response;
}

/*------------------------------------*\
AUTH FUNCTIONS
/*------------------------------------*/

/**
 * Gets the access token, refreshing it if necessary.
 * @returns {Promise<string | null>} The access token.
 */
export async function getAccessToken() {
	let accessToken = localStorage.getItem('accessToken');

	if (!accessToken || isTokenExpired(accessToken)) {
		accessToken = await refreshAccessToken();
	}
	
	return accessToken;
}

/**
 * Sends a request to get a magic link.
 * @param {string} email The email to send the magic link to.
 * @returns {Promise<void>}
 */
export async function requestMagicLink(email) {
	await apiRequest(
		`${get(apiRootUrl)}/v3/auth/magic-link/`,
		{
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ email })
		},
		t('user.sendingMagicLink')
	);
	
	toastDisplay(t('user.magicLinkSent'), 'success');
}

/**
 * Verifies the magic link token and retrieves the access and refresh tokens.
 * @param {string} token The token to verify.
 * @returns {Promise<{mfa_required?: boolean, token?: string, loggedIn?: boolean}>}
 */
export async function verifyMagicLink(token) {
	const data = await apiRequest(
		`${get(apiRootUrl)}/v3/auth/magic-link/verify/`,
		{
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ token })
		},
		t('user.verifyingLogin'),
		t('error.invalidMagicLink')
	);
	
	if (data.mfa_required === 'true') {
		return { mfa_required: true, token: data.token };
	}
	
	storeAuth(data);
	await getUserInfo();
	return { loggedIn: true };
}

/**
 * Function that gets called when the page that the magic link redirects to is loaded.
 * @returns {Promise<{mfa_required?: boolean, token?: string, loggedIn?: boolean} | null>}
 */
export async function handleMagicLinkRedirect() {
	const urlParams = new URLSearchParams(window.location.search);
	const token = urlParams.get('token');

	if (!token) return null;
	
	try {
		return await verifyMagicLink(token);
	} catch (error) {
		console.error('Error verifying magic link:', error);
		return null;
	}
}

// Token refresh state management
/** @type {Promise<string | null> | null} */
let refreshPromise = null;
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;
const REFRESH_RETRY_DELAY = 1000; // 1 second

/**
 * Refreshes the access token using the stored refresh token.
 * @returns {Promise<string | null>}
 */
export async function refreshAccessToken() {
	// Prevent multiple simultaneous refresh attempts
	if (refreshPromise) {
		return refreshPromise;
	}

	refreshPromise = performTokenRefresh();
	const result = await refreshPromise;
	refreshPromise = null;
	return result;
}

/**
 * Internal function to perform the actual token refresh
 * @returns {Promise<string | null>}
 */
async function performTokenRefresh() {
	// Check if user session exists in the store (not localStorage directly)
	const currentUser = get(userSession);
	if (!currentUser?.id) {
		console.log('No user session found, cannot refresh token');
		return null;
	}
	
	const refreshToken = localStorage.getItem('refreshToken');
	if (!refreshToken) {
		console.log('No refresh token found');
		return null;
	}

	// Check if we've exceeded max attempts
	if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
		console.log('Max refresh attempts exceeded, logging out');
		clearAuthState();
		return null;
	}
	
	try {
		refreshAttempts++;
		console.log(`Token refresh attempt ${refreshAttempts}/${MAX_REFRESH_ATTEMPTS}`);
		
		const response = await fetch(`${get(apiRootUrl) || 'api.ordalie.tech'}/v3/token/refresh/`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ refresh_token: refreshToken })
		});
		
		if (!response.ok) {
			const errorText = await response.text();
			console.error('Token refresh failed:', response.status, errorText);
			
			// Only clear auth state if it's a 401/403 (invalid refresh token)
			// For other errors (network issues, 500s), don't log out immediately
			if (response.status === 401 || response.status === 403) {
				console.log('Refresh token is invalid, logging out');
				toastDisplay(t('user.expiredToken'));
				clearAuthState();
				return null;
			}
			
			// For other errors, retry after delay if we haven't exceeded max attempts
			if (refreshAttempts < MAX_REFRESH_ATTEMPTS) {
				console.log(`Retrying token refresh in ${REFRESH_RETRY_DELAY}ms`);
				await new Promise(resolve => setTimeout(resolve, REFRESH_RETRY_DELAY));
				return performTokenRefresh();
			}
			
			// If we've exhausted retries, log out
			console.log('All refresh attempts failed, logging out');
			toastDisplay(t('user.expiredToken'));
			clearAuthState();
			return null;
		}
		
		const { access_token } = await response.json();
		localStorage.setItem('accessToken', access_token);
		
		// Reset attempts counter on success
		refreshAttempts = 0;
		console.log('Token refreshed successfully');
		
		return access_token;
	} catch (error) {
		console.error('Token refresh error:', error);
		
		// For network errors, retry if we haven't exceeded max attempts
		if (refreshAttempts < MAX_REFRESH_ATTEMPTS) {
			console.log(`Retrying token refresh after network error in ${REFRESH_RETRY_DELAY}ms`);
			await new Promise(resolve => setTimeout(resolve, REFRESH_RETRY_DELAY));
			return performTokenRefresh();
		}
		
		// If we've exhausted retries, log out
		console.log('All refresh attempts failed due to network errors, logging out');
		clearAuthState();
		return null;
	}
}

/*------------------------------------*\
OAUTH FUNCTIONS
/*------------------------------------*/

export async function handleOAuthCallback() {
	const urlParams = new URLSearchParams(window.location.search);
	const code = urlParams.get('code');
	const state = urlParams.get('state');

	if (!code || !state) return null;
	
	try {
		const provider = new URLSearchParams(state).get('provider');
		if (!provider) {
			toastDisplay(t('error.generalErrorMsg'), 'error');
			return null;
		}
		return await handleOAuthCallbackServer(code, provider);
	} catch (error) {
		console.error(`Error handling OAuth callback for ${state}:`, error);
		return null;
	}
}

/**
 * Handles the OAuth callback server-side.
 * @param {string} code The code to handle.
 * @param {string} provider The provider to handle.
 * @returns {Promise<{mfa_required?: boolean, token?: string, loggedIn?: boolean}>}
 */
export async function handleOAuthCallbackServer(code, provider) {
	const data = await apiRequest(
		`${get(apiRootUrl)}/v3/auth/${provider}/callback/`,
		{
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ code })
		},
		t('user.signingInWith', { provider: provider.charAt(0).toUpperCase() + provider.slice(1) })
	);
	
	if (data.mfa_required === 'true') {
		return { mfa_required: true, token: data.token };
	}
	
	storeAuth(data);
	return { loggedIn: true };
}

/*------------------------------------*\
MFA FUNCTIONS
/*------------------------------------*/

export async function requestMfaSetup() {
	const token = await getAccessToken();
	const response = await fetch(`${get(apiRootUrl)}/v3/auth/mfa/setup/`, {
		method: 'GET',
		headers: createAuthHeaders(token)
	});
	
	if (!response.ok) throw new Error(await response.text());
	return await response.json();
}

/**
 * Verifies MFA code during setup or login
 * @param {Object} params - MFA verification parameters
 * @param {string} params.code - The MFA code
 * @param {string} [params.token] - Token for login verification
 * @param {string} [params.secret] - Secret for setup verification
 * @returns {Promise<any>}
 */
export async function verifyMfa({ code, token = '', secret = '' }) {
	const payload = secret ? { code, secret } : { code, token };
	/** @type {Record<string, string>} */
	const headers = { 'Content-Type': 'application/json' };

	// If we're in setup mode (secret provided), we need the Authorization header
	if (secret) {
		const accessToken = await getAccessToken();
		if (accessToken) {
			headers.Authorization = `Bearer ${accessToken}`;
		}
	}

	try {
		const response = await fetch(`${get(apiRootUrl)}/v3/auth/mfa/verify/`, {
			method: 'POST',
			headers,
			body: JSON.stringify(payload)
		});
		
		if (!response.ok) throw new Error(await response.text());
		const data = await response.json();

		// Clear any temporary MFA state after successful verification
		if (browser) {
			['mfaToken', 'tempMfaToken', 'mfa_temp_token'].forEach(key => {
				localStorage.removeItem(key);
			});
		}

		if (data.access_token) {
			storeAuth(data);
			await getUserInfo();
		}
		return data;
	} catch (error) {
		// Also clear state on error to prevent token reuse
		if (browser) {
			['mfaToken', 'tempMfaToken', 'mfa_temp_token'].forEach(key => {
				localStorage.removeItem(key);
			});
		}
		throw error;
	}
}

/**
 * Disables MFA for the current user
 * @param {string} code - The current MFA code to verify before disabling
 * @returns {Promise<any>}
 */
export async function disableMfa(code) {
	return await apiRequest(
		`${get(apiRootUrl)}/v3/auth/mfa/disable/`,
		{
			method: 'POST',
			headers: createAuthHeaders(await getAccessToken()),
			body: JSON.stringify({ code })
		}
	);
}

export const LogOut = () => {
	clearAuthState();
	posthog.reset();
	if (browser) window.location.href = '/';
};

/*------------------------------------*\
PAYMENT & SUBSCRIPTIONS FUNCTIONS
/*------------------------------------*/

export const getPaymentMethods = async () => {
	const accessToken = await getAccessToken();
	const response = await fetch(`${getBTOBAPIURL()}/payment_methods/`, {
		method: 'GET',
		headers: createAuthHeaders(accessToken)
	});
	return await response.json();
};

export const getCustomerSubscriptions = async () => {
	try {
		const accessToken = await getAccessToken();
		const response = await fetch(`${getBTOBAPIURL()}/subscriptions/`, {
			method: 'GET',
			headers: createAuthHeaders(accessToken)
		});
		
		if (response.status === 404) return null;
		return await response.json();
	} catch (error) {
		console.error(error);
		return null;
	}
};

export const getBalance = async () => {
	const accessToken = await getAccessToken();
	const response = await fetch(`${getBTOBAPIURL()}/api_credits/`, {
		method: 'GET',
		headers: createAuthHeaders(accessToken)
	});
	
	if (!response.ok) {
		throw new Error('Failed to fetch balance');
	}
	
	return await response.json();
};

/**
 * Refills the user's credits.
 * @param {number} amount The amount of credits to refill.
 * @returns {Promise<any>}
 */
export const refillCredits = async (amount) => {
	const accessToken = await getAccessToken();
	const response = await fetch(`${getBTOBAPIURL()}/add_credits/?amount=${amount}`, {
		method: 'POST',
		headers: createAuthHeaders(accessToken)
	});
	
	if (!response.ok) {
		throw new Error('Failed to initiate credit refill');
	}
	
	return await response.json();
};

export const getCheckoutLink = async () => {
	const accessToken = await getAccessToken();
	const response = await fetch(`${getBTOBAPIURL()}/checkout/`, {
		method: 'GET',
		headers: createAuthHeaders(accessToken)
	});

	return await response.json();
};

export const checkRemainingCredits = async () => {
	const currentUser = JSON.parse(localStorage.getItem('user') ?? '{}');
	if (!currentUser?.id) return null;
	return 8;
};

/**
 * Get Ordalie pro buy link
 * @param {'monthly' | 'yearly'} price_point - The pricing plan
 * @returns {Promise<any>}
 */
export const buyOrdaliePro = async (price_point = 'monthly') => {
	const accessToken = await getAccessToken();
	if (!accessToken) throw new Error('No access token available');

	const url = new URL(`${get(apiRootUrl)}/v3/pro/buy/`);

	try {
		const storedPromo = localStorage?.getItem('ordalie_promo');
		if (storedPromo) {
			const promo = promoConfigs?.find(p => p.id === storedPromo);
			if (promo?.stripeCode) {
				url.searchParams.set('promo_code', promo.stripeCode);
			}
		}

		url.searchParams.set('price_point', price_point);

		// @ts-ignore
		const referral_id = window?.tolt_referral;
		if (referral_id) {
			url.searchParams.set('referral_id', referral_id);
		}

		const response = await fetch(url, {
			method: 'GET',
			headers: createAuthHeaders(accessToken)
		});

		if (!response.ok) throw new Error('Failed to get buy link');
		return await response.json();

	} catch (error) {
		console.error('Error getting buy link:', error);
		throw error;
	}
};

export const getCustomerPortal = async () => {
	const accessToken = await getAccessToken();
	const response = await fetch(`${get(apiRootUrl)}/v3/pro/portal/`, {
		method: 'GET',
		headers: createAuthHeaders(accessToken)
	});
	
	return await response.json();
};

export const getSubscriptions = async () => {
	const accessToken = await getAccessToken();
	if (!accessToken) return null;
	
	const response = await fetch(`${get(apiRootUrl)}/v3/subscriptions/`, {
		method: 'GET',
		headers: createAuthHeaders(accessToken)
	});
	
	const { subscription_status, usage } = await response.json();
	userProStatus.set(subscription_status);
	userFreeUsage.set(usage);
	return { subscription_status, usage };
};

/*------------------------------------*\
USER DATA FUNCTIONS
/*------------------------------------*/

/**
 * Updates the user data.
 * @param {Object} data The data to update (display_name, company_size, user_description, training_progress, source_urls)
 * @returns {Promise<void>}
 */
export const updateUserData = async (data) => {
	try {
		const result = await apiRequest(
			`${get(apiRootUrl)}/v3/user/edit/`,
			{
				method: 'POST',
				headers: createAuthHeaders(await getAccessToken()),
				body: JSON.stringify(data)
			}
		);
		
		const { user, org } = result;
		// if "user_data" inside user is a string then parse it
		if (typeof user.user_data === 'string') {
			user.user_data = JSON.parse(user.user_data);
		}
		userSession.set(user);
		userOrg.set(org);
	} catch (error) {
		toastDisplay(t('error.generalErrorMsg'));
		console.error('Failed to update user data:', error);
	}
};

/**
 * Give feedback
 * @param {Object} data The feedback data
 * @returns {Promise<any>}
 */
export const postFeedback = async (data) => {
	return await apiRequest(
		`${get(apiRootUrl)}/v3/feedback/`,
		{
			method: 'POST',
			headers: createAuthHeaders(await getAccessToken()),
			body: JSON.stringify(data)
		}
	);
};

/*------------------------------------*\
ORGANIZATION FUNCTIONS
/*------------------------------------*/

export const getUserOrgMetadata = async () => {
	const cacheKey = 'userOrgMetadata';
	if (!get(userOrg)?.org_data?.has_vault) return null;
	
	await new Promise((resolve) => setTimeout(resolve, 400));
	
	const data = await apiRequest(
		`${get(apiRootUrl)}/v3/vault/metadata/`,
		{
			method: 'GET',
			headers: createAuthHeaders(await getAccessToken())
		}
	);

	const cacheExpirationTime = 24 * 60 * 60 * 1000;
	const expirationDate = Date.now() + cacheExpirationTime;
	const cacheValue = { value: data, date: expirationDate };
	localStorage.setItem(cacheKey, JSON.stringify(cacheValue));

	return data;
};

/** @returns{Promise<Record<string, any> | null>} */
export const getOrgFileNav = async () => {
	if (!get(userOrg)?.org_data?.has_vault) return null;
	
	try {
		const accessToken = await getAccessToken();
		const response = await fetch(`${get(apiRootUrl)}/v3/vault/file_nav/`, {
			method: 'GET',
			headers: createAuthHeaders(accessToken)
		});
		
		if (response.status === 401 || response.status === 403) {
			return null;
		}
		
		return await response.json();
	} catch (error) {
		if (error instanceof Error && (error.message === 'Unauthorized' || error.message === 'Forbidden')) {
			return null;
		}
		console.error('Failed to fetch org file navigation:', error);
		return null;
	}
};

export const getOrgFolderContent = async (/**@type {string}*/ path) => {
	const data = await apiRequest(
		`${get(apiRootUrl)}/v3/vault/folder_content/?path=${path}`,
		{
			method: 'GET',
			headers: createAuthHeaders(await getAccessToken())
		}
	);
	
	return data.files || [];
};

/**
 * Get a WebSocket token for a given service.
 * @param {string} service - The service to get the token for.
 * @returns {Promise<any>} The WebSocket token response.
 */
export const getWebSocketToken = async (/** @type {string} */ service) => {
	return await apiRequest(
		`${get(apiRootUrl)}/v3/websocket/token/${service}`,
		{
			method: 'GET',
			headers: createAuthHeaders(await getAccessToken())
		}
	);
};

/**
 * @typedef {Object} User
 * @property {string} id - User ID
 * @property {string} email - User's email address
 * @property {boolean} email_verified - Whether the email is verified
 * @property {string} display_name - User's display name
 * @property {string|null} photo_url - URL to user's profile photo
 * @property {string} provider - Authentication provider (e.g., "google", "microsoft")
 * @property {Object} user_data - Additional user data
 */

/**
 * @typedef {Object} UserInfoResponse
 * @property {User} user - User information
 * @property {import('$lib/organisation.svelte.js').Organization} [org] - Organization data if user belongs to one
 * @property {string} [subscription_status] - User's subscription status
 * @property {number} [usage] - Credit usage (for non-premium users)
 * @property {boolean} has_payment_method - Whether user has a payment method set up
 * @property {boolean} mfa_enabled - Whether MFA is enabled for the user
 */

/**
 * Fetches comprehensive user information including organization status,
 * subscription details, and payment method status.
 * 
 * @returns {Promise<UserInfoResponse>} User information response
 * @throws {Error} If the request fails
 */
export async function getUserInfo() {
	try {
		const token = await getAccessToken();
		if (!token) throw new Error('No access token available');
		
		const data = await apiRequest(
			`${get(apiRootUrl)}/v3/user/info/`,
			{
				method: 'GET',
				headers: createAuthHeaders(token)
			}
		);

		// Update stores with the received data
		if (data.user) {
			if (typeof data.user.user_data === 'string') {
				data.user.user_data = JSON.parse(data.user.user_data);
			}
			userSession.set(data.user);
		}

		userOrg.set(data.org || null);
		userProStatus.set(data.subscription_status || 'none');
		userFreeUsage.set(typeof data.usage === 'number' ? data.usage : 0);

		if (browser && window.location.hostname.includes('localhost')) {
			console.log("User info:", data);
		}

		return data;
	} catch (error) {
		console.error('Error fetching user info:', error);
		throw error;
	}
}

/*------------------------------------*\
USER IMAGE FUNCTIONS
/*------------------------------------*/

/**
 * Uploads a user profile image
 * @param {File} imageFile - The image file to upload
 * @returns {Promise<boolean>} - True if successful
 */
export async function uploadUserImage(imageFile) {
	const token = await getAccessToken();
	if (!token) throw new Error('No access token available');

	const formData = new FormData();
	formData.append('image', imageFile);

	const response = await fetch(`${get(apiRootUrl)}/v3/user/upload/image/`, {
		method: 'POST',
		headers: { 'Authorization': `Bearer ${token}` },
		body: formData
	});

	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(`Failed to upload image: ${response.status} ${errorText}`);
	}

	return true;
}

/**
 * Gets the user's profile image
 * @param {string} userId - The user ID
 * @returns {Promise<Blob>} - The image blob
 */
export async function getUserImage(userId) {
	const token = await getAccessToken();
	if (!token) throw new Error('No access token available');

	const response = await fetch(`${get(apiRootUrl)}/v3/user/image/?userId=${userId}`, {
		method: 'GET',
		headers: { 'Authorization': `Bearer ${token}` }
	});

	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(`Failed to get image: ${response.status} ${errorText}`);
	}

	return await response.blob();
}

/**
 * Deletes the user's profile image
 * @returns {Promise<boolean>} - True if successful
 */
export async function deleteUserImage() {
	const token = await getAccessToken();
	if (!token) throw new Error('No access token available');

	const response = await fetch(`${get(apiRootUrl)}/v3/user/image/`, {
		method: 'DELETE',
		headers: { 'Authorization': `Bearer ${token}` }
	});

	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(`Failed to delete image: ${response.status} ${errorText}`);
	}

	return true;
}
